/**
 * Firebase Analytics (GA4) for the web app — SAME property as the mobile app,
 * separate "Web" data stream (G-TGT80CYBM6). Uses the mobile app's event
 * scheme so cross-platform reports unify:
 *   feature_used { feature: snake_case, is_premium?, source? }
 * (services/analytics.ts is the reference for naming conventions.)
 *
 * Disabled in dev so localhost sessions never pollute production data.
 */

import {
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
  setUserProperties,
  type Analytics,
} from 'firebase/analytics'
import { app } from './firebase'

const ENABLED = !import.meta.env.DEV

let analytics: Analytics | null = null

if (ENABLED) {
  isSupported()
    .then((ok) => {
      if (ok) analytics = getAnalytics(app)
    })
    .catch(() => {
      /* ad blockers / unsupported browsers — analytics stays off */
    })
}

export function track(eventName: string, params?: Record<string, unknown>): void {
  if (!analytics) return
  try {
    logEvent(analytics, eventName, { platform: 'web', ...params })
  } catch {
    /* never let analytics break the app */
  }
}

/** Mobile trackFeatureUsed: one bucket, ranked by the `feature` dimension. */
export function trackFeature(feature: string, params?: Record<string, unknown>): void {
  track('feature_used', { feature, ...params })
}

/** SPA route change — GA4 needs manual page_view per navigation. */
export function trackPageView(path: string): void {
  track('page_view', { page_path: path, page_location: window.location.href, page_title: path })
}

export function identifyUser(uid: string | null, isPremium?: boolean): void {
  if (!analytics) return
  try {
    setUserId(analytics, uid)
    if (uid != null && isPremium !== undefined) {
      setUserProperties(analytics, { is_premium: String(isPremium) })
    }
  } catch {
    /* ignore */
  }
}
